import { FolderIcon } from '@heroicons/react/24/outline';

import { MatterLink } from '@/components/entity-links';
import { Badge } from '@/components/ui/catalyst/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { enhancedApi } from '@/services/api/policeDepartmentsService';

interface ActiveMattersSectionProps {
  departmentId: string;
}

const getInvestigationStatusBadge = (status: string) => {
  const configs: Record<
    string,
    { color: 'zinc' | 'blue' | 'amber' | 'red' | 'emerald'; label: string }
  > = {
    need_info: { color: 'zinc', label: 'Need Info' },
    researching: { color: 'blue', label: 'Researching' },
    investigating: { color: 'amber', label: 'Investigating' },
    escalated: { color: 'red', label: 'Escalated' },
    completed_report_received: {
      color: 'emerald',
      label: 'Report Received',
    },
    closed_no_report: { color: 'zinc', label: 'Closed' },
  };

  const config = configs[status] || { color: 'zinc' as const, label: status };

  return <Badge color={config.color}>{config.label}</Badge>;
};

export const ActiveMattersSection = ({
  departmentId,
}: ActiveMattersSectionProps) => {
  const { data: matters, isLoading } =
    enhancedApi.usePoliceDepartmentsActiveMattersQuery({ id: departmentId });

  if (isLoading) {
    return (
      <div className="rounded-lg border bg-white p-5 shadow-sm">
        <div className="mb-4">
          <h2 className="text-sm font-semibold text-gray-900">
            Active Matters
          </h2>
          <p className="mt-0.5 text-xs text-gray-500">
            Loading active matters...
          </p>
        </div>
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  const mattersList = Array.isArray(matters) ? matters : [];

  if (mattersList.length === 0) {
    return (
      <div className="rounded-lg border bg-white p-5 shadow-sm">
        <h2 className="mb-4 text-sm font-semibold text-gray-900">
          Active Matters
        </h2>
        <div className="flex flex-col items-center py-12 text-center">
          <div className="rounded-full bg-gray-100 p-3">
            <FolderIcon className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="mt-4 text-sm font-medium text-gray-900">
            No active matters
          </h3>
          <p className="mt-1 text-xs text-gray-500">
            This department has no ongoing police report investigations
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">
            Active Matters
          </h2>
          <p className="mt-0.5 text-xs text-gray-500">
            {mattersList.length}{' '}
            {mattersList.length === 1 ? 'matter' : 'matters'} with ongoing
            investigations
          </p>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Matter
              </th>
              <th className="px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Firm
              </th>
              <th className="px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Status
              </th>
              <th className="px-3 py-2.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Date
              </th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {mattersList.map((matter) => (
              <tr
                key={matter.matter_id}
                className="border-t border-gray-200 transition-colors hover:bg-gray-50"
              >
                <td className="px-3 py-3 text-sm font-medium">
                  <MatterLink entityId={matter.matter_id}>
                    {matter.matter_name}
                  </MatterLink>
                </td>
                <td className="px-3 py-3 text-sm text-gray-600">
                  {matter.firm_name}
                </td>
                <td className="px-3 py-3">
                  {getInvestigationStatusBadge(matter.investigation_status)}
                </td>
                <td className="px-3 py-3 text-sm text-gray-600">
                  {matter.date_of_loss
                    ? new Date(matter.date_of_loss).toLocaleDateString(
                        'en-US',
                        {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        },
                      )
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
